import { ArrowRightOutlined } from '@ant-design/icons';
import { Card, Alert, Button, List, Tabs, Space } from 'antd';
import { groupBy } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleType } from '../../../api/types';
import getAvailableModules, { type ModuleClass, type CompanySpecs } from './getAvailableModules';

export type ModuleClassSelectionProps = {
    company: CompanySpecs;
    onSelect: (moduleClass: ModuleClass) => void;
    goBack: () => void;
};

const preventToCreateMultipleModule = (module: ModuleClass, company: CompanySpecs) => {
    const preventedList = [ModuleType.LabelsModule, ModuleType.ConsentsAndDeclarations];
    const companyHasPreventedDuplicateModules = company.modules.filter(module =>
        preventedList.find(preventMultiple => module.__typename.includes(preventMultiple))
    );
    if (companyHasPreventedDuplicateModules.length) {
        if (companyHasPreventedDuplicateModules.find(preventedModule => preventedModule.__typename === module.type)) {
            return true;
        }
    }

    return false;
};

const ModuleClassSelection = ({ onSelect, company, goBack }: ModuleClassSelectionProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const moduleGroups = useMemo(() => groupBy('group', getAvailableModules(company, t)), [company, t]);

    const renderList = (modules: ModuleClass[]) => (
        <List<ModuleClass>
            dataSource={modules}
            renderItem={module =>
                !preventToCreateMultipleModule(module, company) ? (
                    <List.Item
                        actions={[
                            <Button
                                disabled={module.disabled}
                                icon={<ArrowRightOutlined />}
                                onClick={() => onSelect(module)}
                                type="link"
                            />,
                        ]}
                    >
                        <List.Item.Meta description={module.description} title={module.label} />
                    </List.Item>
                ) : null
            }
        />
    );

    const renderBody = () => {
        const groupCounts = Object.keys(moduleGroups).length;

        if (groupCounts === 0) {
            return <Alert description="No available modules" message="Error" type="error" showIcon />;
        }

        if (groupCounts === 1) {
            return renderList(Object.values(moduleGroups)[0]);
        }

        return (
            <Tabs tabPosition="left">
                {Object.entries(moduleGroups).map(([group, modules]) => (
                    <Tabs.TabPane key={group} tab={group}>
                        {renderList(modules)}
                    </Tabs.TabPane>
                ))}
            </Tabs>
        );
    };

    return (
        <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
                {renderBody()}
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={goBack} type="default">
                        {t('moduleDetails:addModule.classSelection.cancelButton')}
                    </Button>
                </div>
            </Space>
        </Card>
    );
};

export default ModuleClassSelection;
